// MARK://dhk8/admin-shell/lib/clients/account
// Stripe-style test/live model (Opción A): one Account → two parallel, persistent tenants
// (`liveTenantId` + `testTenantId`). The env-switcher in the shell flips which tenantId the
// service headers carry.
//
// F3.1 / ADR-0009: `getAccount` lee la Account del OWNER del JWT por el grafo
// (platform-tenants `myAccount`). Si el grafo falla, cae a un mock determinístico
// derivado del tenant de la sesión (resiliencia: el shell no debe romperse por esto).
// El `copy-catalog` (go-live) sigue REST (es escritura → F8.x).
import { PREVIEW_STUB, mock } from '../env.js'
import { gqlClient } from '../gql.js'
import { MyAccountQuery, CopyCatalogMutation } from '../queries/platform.queries.js'

export interface AccountInfo {
  accountId: string
  liveTenantId: string
  testTenantId: string
  ownerUserId: string
  /** Slug humano de la cuenta (base de los enlaces públicos compartibles). */
  slug: string
}

/** Resolve the Account for the current owner. Falls back to a mock derived from the session's
 *  tenantId (treating it as the test tenant) when the endpoint is missing. */
export async function getAccount(fallbackTenantId: string, signal?: AbortSignal): Promise<AccountInfo> {
  const mockAccount: AccountInfo = {
    accountId: 'acct_' + fallbackTenantId.replace(/^tnt_/, ''),
    testTenantId: fallbackTenantId,
    liveTenantId: fallbackTenantId,
    ownerUserId: 'usr_owner',
    slug: '',
  }
  if (PREVIEW_STUB) return mock(mockAccount)
  try {
    const ctx = signal ? { fetchOptions: { signal } } : undefined
    const res = await gqlClient.query(MyAccountQuery, {}, ctx).toPromise()
    if (res.error) throw res.error
    const a = res.data?.myAccount
    if (!a) return mockAccount // sin cuenta para el owner → fallback
    return {
      accountId: a.accountId,
      liveTenantId: a.liveTenantId,
      testTenantId: a.testTenantId,
      ownerUserId: a.ownerUserId,
      slug: a.slug ?? '',
    }
  } catch {
    return mockAccount // grafo no disponible → TODO(backend)
  }
}

/** First-time go-live convenience: copy the test catalog into the live tenant.
 *  F6.2 R3c / ADR-0008 + ADR-0019: por el grafo (platform-tenants copyCatalog).
 *  Owner-guard server-side: el caller (ctx.userId) debe ser el dueño de la Account
 *  del liveTenantId. El backend sigue stubbed (copied=false) hasta que la copia real
 *  se implemente; el evento catalog_copy_requested ya se emite. */
export async function copyCatalogFromTestToLive(liveTenantId: string, testTenantId: string): Promise<void> {
  if (PREVIEW_STUB) return mock(undefined, 500)
  const res = await gqlClient.mutation(CopyCatalogMutation, { liveTenantId, testTenantId }).toPromise()
  if (res.error) throw res.error
}
